/**
 * Free gift on qualifying spend.
 *
 * The threshold is held in the shop's own currency (see the Free gift section).
 * A cart in another presentment currency is converted with Shopify's own market
 * rate before it is compared, so MXN, JPY and AED carts all have to reach the
 * same real spend. If no rate can be established the cart does NOT qualify —
 * comparing raw numbers is what handed out gifts on small foreign orders.
 *
 * The gift is also reconciled to an exact quantity on every cart change: every
 * line holding the gift variant is counted and anything above the allowance is
 * corrected. That is what stops the duplicate caps.
 */
(function () {
  'use strict';

  var DEFAULTS = {
    enabled: true,
    variantId: 57231685255433,
    threshold: 500,
    shopCurrency: 'USD',
    maxQty: 1,
    tiered: false,
    overrides: '',
    debug: false
  };
  var RATE_API = 'https://open.er-api.com/v6/latest/';
  var GIFT_KEY = 'free_gift';

  var C = DEFAULTS;
  var VARIANT = 0;
  var busy = false;
  var ratesPromise = null;
  var originalFetch = window.fetch.bind(window);

  function read() {
    var node = document.getElementById('md-gift-config');
    if (!node) return DEFAULTS;
    try {
      var raw = JSON.parse(String(node.textContent).replace(/,(\s*[\]}])/g, '$1'));
      var out = Object.assign({}, DEFAULTS, raw);
      out.threshold = parseFloat(String(out.threshold).replace(/[^\d.]/g, '')) || DEFAULTS.threshold;
      out.variantId = parseInt(String(out.variantId).replace(/\D/g, ''), 10) || DEFAULTS.variantId;
      out.maxQty = Math.max(1, parseInt(out.maxQty, 10) || 1);
      return out;
    } catch (e) { return DEFAULTS; }
  }

  function log() {
    if (!C.debug || !window.console) return;
    console.log.apply(console, ['[FreeGift]'].concat(Array.prototype.slice.call(arguments)));
  }

  function root() {
    return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  }

  /* Fixed amounts per currency: "MXN 9500" per line. */
  function override(code) {
    if (!C.overrides || !code) return null;
    var lines = String(C.overrides).split(/[\n;]/);
    for (var i = 0; i < lines.length; i++) {
      var m = lines[i].match(/([A-Za-z]{3})\s*[:=]?\s*([\d.,]+)/);
      if (!m) continue;
      if (m[1].toUpperCase() !== String(code).toUpperCase()) continue;
      var n = parseFloat(m[2].replace(/,/g, ''));
      if (!isNaN(n)) return n;
    }
    return null;
  }

  /* Shopify's presentment rate: cart-currency units per unit of shop currency. */
  function shopifyRate(code) {
    var c = window.Shopify && Shopify.currency;
    if (!c) return null;
    if (c.active && String(c.active).toUpperCase() !== String(code).toUpperCase()) return null;
    var rate = parseFloat(c.rate);
    return (!rate || isNaN(rate) || rate <= 0) ? null : rate;
  }

  function fxRate(code) {
    if (!ratesPromise) {
      ratesPromise = originalFetch(RATE_API + encodeURIComponent(C.shopCurrency || 'USD'))
        .then(function (r) { return r.json(); })
        .then(function (d) { return (d && d.rates) || {}; })
        .catch(function () { return {}; });
    }
    return ratesPromise.then(function (rates) {
      var r = parseFloat(rates[String(code).toUpperCase()]);
      return (!r || isNaN(r) || r <= 0) ? null : r;
    });
  }

  /* The threshold expressed in the cart's own currency, or null when unknown. */
  function thresholdIn(code) {
    var fixed = override(code);
    if (fixed != null) return Promise.resolve(fixed);
    if (!code) return Promise.resolve(null);
    if (String(code).toUpperCase() === String(C.shopCurrency).toUpperCase()) {
      return Promise.resolve(C.threshold);
    }
    var rate = shopifyRate(code);
    if (rate) return Promise.resolve(C.threshold * rate);
    return fxRate(code).then(function (r) { return r == null ? null : C.threshold * r; });
  }

  function isGift(item) {
    if (VARIANT && Number(item.variant_id) === VARIANT) return true;
    return !!(item.properties && item.properties._gift === GIFT_KEY);
  }

  function cartJson() {
    return originalFetch(root() + 'cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function change(body) {
    body.sections = 'cart-drawer,header';
    body.sections_url = window.location.pathname;
    return originalFetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); }).catch(function () { return null; });
  }

  function addGift(qty) {
    return originalFetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: VARIANT, quantity: qty, properties: { _gift: GIFT_KEY } }],
        sections: 'cart-drawer,header',
        sections_url: window.location.pathname
      })
    }).then(function (r) { return r.json(); }).catch(function () { return null; });
  }

  function applySections(sections) {
    if (!sections) return;
    var tmp = document.createElement('div');
    if (sections['cart-drawer']) {
      tmp.innerHTML = sections['cart-drawer'];
      var fresh = tmp.querySelector('#cart-drawer');
      var cur = document.getElementById('cart-drawer');
      if (fresh && cur) {
        var wasOpen = cur.hasAttribute('data-open');
        cur.replaceWith(fresh);
        if (wasOpen) fresh.setAttribute('data-open', '');
      }
    }
    if (sections['header']) {
      tmp.innerHTML = sections['header'];
      var a = tmp.querySelector('[data-cart-count]');
      var b = document.querySelector('[data-cart-count]');
      if (a && b) b.textContent = a.textContent;
    }
  }

  async function evaluate(cartData) {
    if (busy || C.enabled === false || !VARIANT) return;
    busy = true;
    try {
      var cart = cartData || await cartJson();
      if (!cart || !Array.isArray(cart.items)) return;

      var gifts = cart.items.filter(isGift);
      var giftQty = gifts.reduce(function (n, i) { return n + i.quantity; }, 0);
      var spend = cart.items.reduce(function (n, i) { return isGift(i) ? n : n + i.line_price; }, 0) / 100;

      var code = cart.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || C.shopCurrency;
      var need = await thresholdIn(code);

      var want = 0;
      if (need != null && need > 0) {
        var steps = Math.floor(spend / need);
        if (steps >= 1) want = Math.min(C.tiered ? steps : 1, C.maxQty);
      } else {
        log('no rate for ' + code + ' — cart cannot qualify');
      }

      log(spend + ' ' + code + ' | needs ' + need + ' | gift ' + giftQty + ' → ' + want);

      if (giftQty === want && gifts.length <= 1) return;

      var result = null;
      /* Collapse any duplicate gift lines first, then set the single remaining one. */
      for (var i = gifts.length - 1; i >= 1; i--) {
        result = await change({ id: gifts[i].key, quantity: 0 });
      }
      if (gifts.length >= 1) {
        result = await change({ id: gifts[0].key, quantity: want });
      } else if (want > 0) {
        result = await addGift(want);
      }

      if (result && result.sections) applySections(result.sections);
    } catch (err) {
      if (window.console) console.error('[FreeGift]', err);
    } finally {
      busy = false;
    }
  }

  function attach() {
    ['cart:updated', 'cart:item-added', 'cart:item-removed', 'shopify:cart:change', 'cart:refresh', 'cart:build']
      .forEach(function (evt) {
        document.addEventListener(evt, function () { evaluate(); });
        window.addEventListener(evt, function () { evaluate(); });
      });

    /* The theme adds to cart with a direct fetch and no event, so the call is
       observed here. Our own calls use the untouched original fetch. */
    window.fetch = async function () {
      var args = Array.prototype.slice.call(arguments);
      var result = await originalFetch.apply(null, args);
      var url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url) || '';
      var mutated = url.indexOf('/cart/add') > -1 || url.indexOf('/cart/change') > -1 ||
        url.indexOf('/cart/update') > -1 || url.indexOf('/cart/clear') > -1;
      if (mutated && !busy) {
        result.clone().json().then(function (d) {
          if (d && Array.isArray(d.items)) evaluate(d); else evaluate();
        }).catch(function () { evaluate(); });
      }
      return result;
    };
  }

  function boot() {
    C = read();
    VARIANT = Number(C.variantId) || 0;
    attach();
    evaluate();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
